"use client"

import { useRouter } from "next/navigation"
import { StatusBadge, type InvoiceStatus } from "@/components/status-badge"
import { ActionRequiredBadge } from "@/components/action-required-badge"
import { DepositBadge } from "./DepositBadge"
import { InstallmentBadge } from "./InstallmentBadge"
import type { BackendStatus, PaymentMode } from "@/features/invoices/types"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { MoreHorizontal } from "lucide-react"

import { InvoiceActionMenu } from "./InvoiceActionMenu"

export interface Invoice {
  id: string
  invoiceNumber: string
  status: InvoiceStatus | BackendStatus
  customer: {
    name: string
    email: string
    company: string
  }
  course: string
  expirationDate: Date
  amount: number
  /**
   * Discount-inclusive price the customer pays (gross `amount` minus the
   * full standard + named discount cascade). This is the figure shown on
   * the list and home surfaces — `amount` is retained for callers that
   * need the pre-discount subtotal.
   */
  netAmount: number
  currency: string
  /**
   * Computed payment mode for the row. Drives the small "Deposit"
   * indicator next to the status badge. Defaults to "full" for rows the
   * backend hasn't tagged (fixture back-compat).
   */
  paymentMode: PaymentMode
  /**
   * Whether the customer still owes a step before Stripe can collect
   * (3-D Secure, a voucher, a bank transfer). Drives the secondary
   * "Customer action required" indicator next to the status badge.
   * Defaults to false for fixtures the backend hasn't tagged.
   */
  customerActionRequired?: boolean
  /**
   * Total payments on the row's installment plan, `null` for every other
   * mode. Rendered on the `InstallmentBadge` so the rep can read the
   * number they just filtered and sorted on.
   */
  installmentCount?: number | null
  /** Payments still to collect; `0` on a fully-settled plan. */
  installmentsRemaining?: number | null
  createdAt: Date
  updatedAt: Date
  /**
   * PK of the user who created this row. Used by the action menu to
   * decide whether the current user is allowed to mutate this invoice
   * (Sales Reps may only act on invoices they created).
   */
  createdById?: number
  hubspotDealUrl?: string
}

export type InvoiceRowClickBehavior = "navigate" | "select"

interface InvoiceTableProps {
  invoices: Invoice[]
  selectable?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  rowClickBehavior?: InvoiceRowClickBehavior
}

export function InvoiceTable({
  invoices,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  rowClickBehavior,
}: InvoiceTableProps) {
  const router = useRouter()
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) return "Today"
    if (days === 1) return "1d ago"
    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? invoices.map(i => i.id) : [])
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedIds, id])
      } else {
        onSelectionChange(selectedIds.filter(i => i !== id))
      }
    }
  }

  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length
  const someSelected = selectedIds.length > 0 && selectedIds.length < invoices.length

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-surface-alt hover:bg-surface-alt">
            {selectable && (
              <TableHead className="w-12">
                <Checkbox 
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  className={someSelected ? "opacity-50" : ""}
                />
              </TableHead>
            )}
            <TableHead className="w-24">Status</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Course</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-12">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => {
            const handleRowClick = () => {
              if (rowClickBehavior === "navigate") {
                router.push(`/invoices/${invoice.id}`)
              } else if (rowClickBehavior === "select" && onSelectionChange) {
                const isSelected = selectedIds.includes(invoice.id)
                handleSelectOne(invoice.id, !isSelected)
              }
            }
            const handleRowKeyDown = (
              event: React.KeyboardEvent<HTMLTableRowElement>,
            ) => {
              if (!rowClickBehavior) return
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                handleRowClick()
              }
            }
            const stopClick = (event: React.MouseEvent) => {
              event.stopPropagation()
            }
            const rowInteractive = Boolean(rowClickBehavior)
            return (
              <TableRow
                key={invoice.id}
                className={
                  rowInteractive
                    ? "hover:bg-muted/50 cursor-pointer"
                    : "hover:bg-muted/50"
                }
                onClick={rowInteractive ? handleRowClick : undefined}
                onKeyDown={rowInteractive ? handleRowKeyDown : undefined}
                tabIndex={rowInteractive ? 0 : undefined}
                role={rowInteractive ? "button" : undefined}
                aria-label={
                  rowInteractive
                    ? rowClickBehavior === "navigate"
                      ? `Open invoice ${invoice.invoiceNumber}`
                      : `Select invoice ${invoice.invoiceNumber}`
                    : undefined
                }
              >
                {selectable && (
                  <TableCell onClick={stopClick}>
                    <Checkbox
                      checked={selectedIds.includes(invoice.id)}
                      onCheckedChange={(checked) =>
                        handleSelectOne(invoice.id, !!checked)
                      }
                      aria-label={`Select invoice ${invoice.invoiceNumber}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={invoice.status} />
                    {invoice.paymentMode === "deposit" && (
                      <DepositBadge testId="deposit-indicator" />
                    )}
                    {invoice.paymentMode === "installments" && (
                      <InstallmentBadge
                        testId="installment-indicator"
                        count={invoice.installmentCount}
                        remaining={invoice.installmentsRemaining}
                      />
                    )}
                    {invoice.customerActionRequired && (
                      <ActionRequiredBadge testId="action-required-indicator" />
                    )}
                  </div>
                </TableCell>
              <TableCell>
                <div>
                  <p className="text-body font-medium text-foreground">
                    {invoice.customer.company || invoice.customer.name}
                  </p>
                  <p className="text-caption text-muted-foreground">
                    {invoice.customer.email}
                  </p>
                </div>
              </TableCell>
              <TableCell className="text-body text-text-secondary">
                {invoice.course}
              </TableCell>
              <TableCell className="text-right text-body font-medium tabular-nums">
                {formatCurrency(invoice.netAmount, invoice.currency)}
              </TableCell>
              <TableCell className="text-body text-text-secondary">
                {formatDate(invoice.createdAt)}
              </TableCell>
                <TableCell onClick={stopClick}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <InvoiceActionMenu
                      invoiceId={invoice.id}
                      invoiceNumber={invoice.invoiceNumber}
                      status={invoice.status as BackendStatus}
                      paymentMode={invoice.paymentMode}
                      createdById={invoice.createdById}
                      hubspotDealUrl={invoice.hubspotDealUrl}
                    />
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
